// Student Editor - PartDPage5 (Overview - Mixed Editable/Read-only)
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartDPage5 = () => {
  const { activity } = useActivityContext();

  // Form State
  const [formData, setFormData] = useState({
    learner_reflection: {
      overall_rating: 0,
      self_appreciation: '',
      performance_levels: { awareness_level: '', sensitivity_level: '', creativity_level: '' }
    },
    peer_feedback: {
      overall_rating: 0,
      peer_appreciation: '',
      performance_levels: { awareness_level: '', sensitivity_level: '', creativity_level: '' }
    }
  });

  // Teacher Data (read-only)
  const [teacherData, setTeacherData] = useState({
    overall_rating: 0,
    appreciation_comments: '',
    performance_levels: { awareness_level: '', sensitivity_level: '', creativity_level: '' }
  });

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const saveTimeoutRef = useRef(null);

  // Load Data
  useEffect(() => {
    // Load Student Data
    if (activity?.my_response_part_d?.page_5) {
      const loadedData = activity.my_response_part_d.page_5;
      setFormData({
        learner_reflection: {
          overall_rating: loadedData.learner_reflection?.overall_rating || 0,
          self_appreciation: loadedData.learner_reflection?.self_appreciation || '',
          performance_levels: loadedData.learner_reflection?.performance_levels || { awareness_level: '', sensitivity_level: '', creativity_level: '' }
        },
        peer_feedback: {
          overall_rating: loadedData.peer_feedback?.overall_rating || 0,
          peer_appreciation: loadedData.peer_feedback?.peer_appreciation || '',
          performance_levels: loadedData.peer_feedback?.performance_levels || { awareness_level: '', sensitivity_level: '', creativity_level: '' }
        }
      });
    }

    // Load Teacher Data
    if (activity?.part_d?.page_5?.teacher_fields) {
      const loadedTeacherData = activity.part_d.page_5.teacher_fields;
      setTeacherData({
        overall_rating: loadedTeacherData.overall_rating || 0,
        appreciation_comments: loadedTeacherData.appreciation_comments || '',
        performance_levels: loadedTeacherData.performance_levels || { awareness_level: '', sensitivity_level: '', creativity_level: '' }
      });
    }
  }, [activity]);

  // Handle Change
  const handleChange = (section, field, value, subField = null) => {
    const newFormData = { ...formData };

    if (subField) {
      newFormData[section][field][subField] = value;
    } else {
      newFormData[section][field] = value;
    }

    setFormData(newFormData);

    // Debounced Save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges(newFormData), 1000);
  };

  // Save Function
  const saveChanges = async (dataToSave) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-d/page/5/student-fields`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
    } finally {
      setSaving(false);
    }
  };

  // Render Lined Paper (Editable or Read-only)
  const renderLinedPaper = (text, title, section, fieldName, isEditable) => (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '200px',
      backgroundColor: '#fff',
      backgroundImage: 'linear-gradient(#999 1px, transparent 1px)',
      backgroundSize: '100% 24px',
      border: '1px solid #333',
      borderRadius: '2px',
      padding: '0',
      overflow: 'hidden',
      boxShadow: '2px 2px 5px rgba(0,0,0,0.1)'
    }}>
      {/* Red margin line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '30px',
        bottom: 0,
        width: '1px',
        backgroundColor: '#ff0000',
        opacity: 0.5,
        pointerEvents: 'none'
      }} />

      {/* Holes */}
      <div style={{ position: 'absolute', top: '10px', left: '10px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '40px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '70px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />
      <div style={{ position: 'absolute', top: '10px', left: '100px', width: '10px', height: '10px', borderRadius: '50%', border: '1px solid #999', backgroundColor: '#fff' }} />

      {/* Curled Corner Effect */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '40px',
        height: '40px',
        background: 'linear-gradient(135deg, transparent 50%, #f0f0f0 50%)',
        boxShadow: '-2px -2px 5px rgba(0,0,0,0.1)',
        borderTop: '1px solid #ccc',
        borderLeft: '1px solid #ccc',
        borderBottomRightRadius: '2px',
        pointerEvents: 'none',
        zIndex: 10
      }} />

      {/* Content */}
      <div style={{
        padding: '30px 10px 10px 40px',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          fontWeight: 'bold',
          marginBottom: '5px',
          lineHeight: '1.2',
          fontFamily: 'Comic Sans MS, cursive, sans-serif',
          fontSize: '12px',
          color: '#333'
        }}>
          {title}
        </div>

        {isEditable ? (
          <textarea
            value={text}
            onChange={(e) => handleChange(section, fieldName, e.target.value)}
            style={{
              width: '100%',
              flex: 1,
              border: 'none',
              background: 'transparent',
              resize: 'none',
              outline: 'none',
              fontSize: '14px',
              lineHeight: '24px',
              fontFamily: 'Comic Sans MS, cursive, sans-serif',
              color: '#333',
              marginTop: '-2px' // Fine-tune alignment with lines
            }}
            placeholder="Type here..."
          />
        ) : (
          <div style={{
            fontSize: '14px',
            lineHeight: '24px',
            fontFamily: 'Comic Sans MS, cursive, sans-serif',
            color: '#333',
            whiteSpace: 'pre-wrap'
          }}>
            {text || 'No comments provided.'}
          </div>
        )}
      </div>
    </div>
  );

  // Render Performance Grid (Editable or Read-only)
  const renderPerformanceGrid = (levels, section, isEditable) => {
    const categories = ['Awareness', 'Sensitivity', 'Creativity'];
    const performanceLevels = [
      { label: 'Beginner\n(0-1)', value: 'Beginner' },
      { label: 'Proficient\n(2-3)', value: 'Proficient' },
      { label: 'Advanced\n(4-5)', value: 'Advanced' }
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {categories.map((cat) => (
          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* Category Label */}
            <div style={{
              backgroundColor: '#2C3E50',
              color: 'white',
              padding: '10px',
              width: '110px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '12px',
              borderRadius: '4px'
            }}>
              {cat}
            </div>

            {/* Levels */}
            {performanceLevels.map((level, idx) => {
              const isChecked = levels?.[`${cat.toLowerCase()}_level`] === level.value;

              return (
                <div
                  key={idx}
                  style={{
                    position: 'relative',
                    width: '70px',
                    height: '70px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: isEditable ? 'pointer' : 'default'
                  }}
                  onClick={() => {
                    if (isEditable) {
                      handleChange(section, 'performance_levels', level.value, `${cat.toLowerCase()}_level`);
                    }
                  }}
                >
                  {/* Checkbox */}
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #333',
                    backgroundColor: 'white',
                    marginBottom: '-10px',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    borderRadius: '3px'
                  }}>
                    {isChecked ? '✓' : ''}
                  </div>

                  {/* Circle */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    backgroundColor: '#F9E79F',
                    border: '2px solid #F1C40F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: '10px',
                    padding: '4px',
                    boxSizing: 'border-box',
                    lineHeight: '1.2',
                    fontWeight: 'bold',
                    transition: 'transform 0.1s',
                    transform: isChecked ? 'scale(1.05)' : 'scale(1)'
                  }}>
                    {level.label}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      fontFamily: "'Poppins', Arial, sans-serif",
      boxSizing: 'border-box'
    }}>
      {/* Save Indicator */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saving ? '#FFC107' : '#4CAF50',
          color: saving ? 'black' : 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontWeight: 'bold'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Main Header */}
      <div style={{
        backgroundColor: '#F4824C',
        color: 'white',
        textAlign: 'center',
        padding: '15px',
        marginBottom: '5px',
        borderRadius: '4px'
      }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Overview</div>
        <div style={{ fontSize: '14px', marginTop: '5px' }}>Tabulate all the feedback for the classroom interaction on this page.</div>
      </div>

      {/* 1. Teacher Assessment (Read-only) */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '8px 20px',
          fontWeight: 'bold',
          fontSize: '16px',
          display: 'inline-block',
          marginBottom: '15px',
          borderRadius: '4px'
        }}>
          Teacher Assessment
        </div>
        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            {renderLinedPaper(teacherData.appreciation_comments, "Write some words of appreciation for the learner about their performance.", null, 'appreciation_comments', false)}
          </div>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'right', color: '#666' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(teacherData.performance_levels, null, false)}
          </div>
        </div>
      </div>

      {/* 2. Learner Reflection (Editable) */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{
          backgroundColor: '#4A7BA7',
          color: 'white',
          padding: '8px 20px',
          fontWeight: 'bold',
          fontSize: '16px',
          display: 'inline-block',
          marginBottom: '15px',
          borderRadius: '4px'
        }}>
          Learner Reflection
        </div>
        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            {renderLinedPaper(formData.learner_reflection.self_appreciation, "Write some words of appreciation for yourself.", 'learner_reflection', 'self_appreciation', true)}
          </div>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'right', color: '#666' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(formData.learner_reflection.performance_levels, 'learner_reflection', true)}
          </div>
        </div>
      </div>

      {/* 3. Peer Feedback (Editable) */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{
          backgroundColor: '#F4B400',
          color: 'black',
          padding: '8px 20px',
          fontWeight: 'bold',
          fontSize: '16px',
          display: 'inline-block',
          marginBottom: '15px',
          borderRadius: '4px'
        }}>
          Peer Feedback
        </div>
        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            {renderLinedPaper(formData.peer_feedback.peer_appreciation, "Write some words of appreciation for your peer about their performance.", 'peer_feedback', 'peer_appreciation', true)}
          </div>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'right', color: '#666' }}>
              Tick (✓) the final performance level based on the score.
            </div>
            {renderPerformanceGrid(formData.peer_feedback.performance_levels, 'peer_feedback', true)}
          </div>
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        textAlign: 'center',
        marginTop: '40px',
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold'
      }}>
        35
      </div>
    </div>
  );
};

export default PartDPage5;
